import { useEffect, useRef } from 'react';

// Browser Back integration for the SPA shell. The app navigates by
// React state (drill into a tank, open settings, open param history) —
// none of which touch the browser history stack, so pressing Back
// pops the single /app entry and leaves the site. This hook makes the
// hardware/browser Back button unwind those in-app layers one at a
// time instead.
//
// Model: `layers` is an ordered list (shallowest → deepest) of
// { open, close } — each a currently-visible navigation layer and how
// to dismiss it. The hook:
//   - pushes a history entry tagged with its depth whenever the open
//     depth INCREASES (a layer opened),
//   - on popstate, reads the target depth from the (now-current)
//     history entry and closes layers deepest-first until the open
//     depth matches — so a single Back closes one layer and a
//     history.go(-N) (the in-app "Home" jump) closes N at once.
//
// In-app dismiss affordances (a settings Close button, the Home tab)
// should call window.history.back() / history.go(-n) rather than
// mutating state directly, so the history stack and the UI stay in
// lockstep. openLayer() below is the matching "go deeper" primitive.

// Pure: given the ordered layers and a target depth, return the
// indices to close (deepest-first) to reach that depth. Exported for
// testing the unwind order without a DOM.
export function layersToClose(layers, targetDepth) {
  const openIdx = [];
  for (let i = 0; i < layers.length; i++) if (layers[i].open) openIdx.push(i);
  const excess = openIdx.length - Math.max(0, targetDepth);
  if (excess <= 0) return [];
  // close the deepest `excess` open layers
  return openIdx.slice(openIdx.length - excess).reverse();
}

// How the history stack follows the open depth: push one entry per layer
// opened, and when layers close by state (a bottom-bar tap, Add a tank from
// Account) step back over their entries too. Left behind, the next in-app
// Back landed on an entry of the same depth and closed nothing; after a tab
// tap it took two or three taps (review of UI step 3, 2026-10-21).
// Positive: entries to push; negative: entries to go back over.
export function historySteps(pushed, open) {
  return open - pushed;
}

export function useHistoryBackStack(layers) {
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const pushedDepthRef = useRef(0);

  const openDepth = layers.reduce((n, l) => n + (l.open ? 1 : 0), 0);

  // Push a history entry per new layer when the open depth grows. A
  // shrink (from popstate or a programmatic history.back) is handled
  // by the popstate listener — never pushed here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pushed = pushedDepthRef.current;
    const steps = historySteps(pushed, openDepth);
    if (steps > 0) {
      for (let d = pushed + 1; d <= openDepth; d++) {
        window.history.pushState({ aqsDepth: d }, '');
      }
    } else if (steps < 0) {
      // Closed by state, not by Back: the popstate this causes finds the
      // open depth already at its target and closes nothing more.
      window.history.go(steps);
    }
    pushedDepthRef.current = openDepth;
  }, [openDepth]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = (e) => {
      const targetDepth = Number(e.state?.aqsDepth) || 0;
      const toClose = layersToClose(layersRef.current, targetDepth);
      for (const i of toClose) layersRef.current[i].close();
      pushedDepthRef.current = targetDepth;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Jump straight to the base view (the in-app "Home" / drill-out
  // affordance) by popping every pushed entry at once. The popstate
  // listener closes whatever's still open; close() is idempotent so
  // it's safe even when the caller already reset state directly.
  const resetToBase = () => {
    if (typeof window === 'undefined') return;
    const n = pushedDepthRef.current;
    if (n > 0) {
      // Already on its way to the base: the layers the caller closes by
      // state in the same tap must not step back a second time.
      pushedDepthRef.current = 0;
      window.history.go(-n);
    }
  };

  return { resetToBase };
}

// Go one layer deeper. Pairs with useHistoryBackStack: pushState here,
// the effect reconciles pushedDepth. Use for drill-in / open-settings
// so the new layer is Back-dismissable.
export function pushHistoryLayer() {
  if (typeof window === 'undefined') return;
  // The depth effect pushes the actual tagged entry; this is a no-op
  // placeholder kept for call-site symmetry where a component wants to
  // signal "going deeper" explicitly. The effect is the source of
  // truth, so we don't double-push here.
}

// Pop `n` layers via the browser, letting the popstate listener do the
// closing. n defaults to 1 (a single Back).
export function goBackLayers(n = 1) {
  if (typeof window === 'undefined') return;
  const steps = Math.max(1, Math.floor(n));
  window.history.go(-steps);
}
